/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React, { useState, useEffect } from 'react';
import { CALL_TYPE_IN, CALL_TYPE_OUT, STATUS_BLOCKED, STATUS_OPEN, ALL, CALL_TYPE_ALL } from '../../../Constants';
import { allowedBlockStatuses, allowedBlockCallTypes, filterAllowed } from '../../../Helper';
import { IMPORT_HOVER_UPLOAD, IMPORT_HOVER_DOWNLOAD } from '../Category';
import Switch from '../../Switch';
import '../../../styles.css';
import { useTranslation } from 'react-i18next';
import DesktopDataGrid from '../../DesktopDataGrid';
import Select from '../../Select';
import SelectWrapper from '../../SelectWrapper';

export default function Category(props) {
  const { t } = useTranslation();
  const inputNameRef = React.useRef(null);
  const [isSearchFormVisible, setIsSearchFormVisible] = useState(false);
  const [importHover, setImportHover] = useState(null);
  // committed search term (set on button/Enter) — used to filter the loaded rows client-side
  const [committedQuery, setCommittedQuery] = useState('');

  const runSearch = () => {
    setCommittedQuery((props.searchInput.number || '').trim());
    props.handleSearchSubmit();   // also ask the server (in case it filters across pages)
  };
  const clearSearch = () => {
    setCommittedQuery('');
    props.changeSearchFilters("", "number");
    props.handleSearchSubmit({ ...props.searchInput, number: "" });
  };

  // rows actually shown: filter the loaded page by the committed query as a reliable fallback.
  // numbers are stored international (e.g. 97255...), so a query like "055" is also matched
  // against its international form ("97255...").
  const searchCandidates = (() => {
    const q = committedQuery.replace(/[^0-9*]/g, '');
    if (!q) return [];
    const cands = [q];
    if (q.startsWith('0')) cands.push('972' + q.slice(1));   // 055... → 97255...
    else if (q.startsWith('972')) cands.push('0' + q.slice(3)); // 97255... → 055...
    return cands;
  })();
  const displayRows = searchCandidates.length
    ? props.rows.filter((r) => { const n = String(r.number || ''); return searchCandidates.some((c) => n.includes(c)); })
    : props.rows;
  const displayCount = committedQuery ? displayRows.length : props.rowCount;

  useEffect(() => {
    document.body.addEventListener('click', (e) => {
      if (
        e.target.closest("body") === null
        || e.target.closest(".body") !== null
        || e.target.closest(".header") !== null
        || e.target.closest(".expansions-wrapper") !== null
        || e.target.closest("div[role='presentation']") !== null
        || e.target.closest(".grid-wrapper") !== null
        || e.target.closest(".add-form-wrapper") !== null
      ) { return; }
      props.setIsAddFormVisible(false);
    });
  }, []);

  const columns = [
    { field: 'number', headerName: t('Number') },
    {
      field: 'active', headerName: t('Is Active'),
      renderCell: (row) => (
        row.active === 0
          ? <span className="cat-cell-active cat-cell-active--off" title={t('Is Active')}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></span>
          : <span className="cat-cell-active cat-cell-active--on" title={t('Is Active')}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>
      )
    },
    {
      field: 'status', headerName: t('Status'),
      renderCell: (params) => (
        <span className={'cat-cell-status ' + (params.status === STATUS_BLOCKED ? 'cat-cell-status--blocked' : 'cat-cell-status--open')}>
          {params.status === STATUS_BLOCKED ? t("Blocked") : t("Open")}
        </span>
      )
    },
    {
      field: 'call_type', headerName: t('Call Type'),
      valueGetter: (params) => {
        if (params.call_type === CALL_TYPE_OUT) return t("Outgoing");
        if (params.call_type === CALL_TYPE_IN)  return t("Incoming");
        return t("Both");
      }
    },
    { field: 'note', headerName: t('Note') }
  ];

  if (props.selectedCategory.isOwner) {
    columns.push({
      field: 'edit', headerName: '',
      renderCell: (cellValues) => (
        <button className='cat-line-act cat-line-act--edit' title={t('Edit')} onClick={() => props.handleEditClick(cellValues)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        </button>
      )
    });
    columns.push({
      field: 'delete', headerName: '',
      renderCell: (cellValues) => (
        <button className='cat-line-act cat-line-act--del' title={t('Delete 2')} onClick={() => props.handleDeleteRow(cellValues)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      )
    });
  }

  const renderRowExpansion = (cellValues) => (
    <>
      <div className='item number'>
        <input type="text" className='underline' name="number" value={cellValues.number} disabled />
      </div>
      <div className='item'>
        <Switch type="boolean" name="active"
          value={props.editBlockValues[cellValues.id]?.active ?? cellValues.active}
          mainValue={1}
          handleClick={(v) => props.handleChangeEditValue(v, "active", { ...cellValues, ...(props.editBlockValues[cellValues.id] ?? {}) })}
          style={{ width: "50px" }}
        />
      </div>
      <div className='item'>
        <Switch type="text" name="status"
          values={filterAllowed({ [STATUS_BLOCKED]: t("Blocked"), [STATUS_OPEN]: t("Open") }, allowedBlockStatuses(), props.editBlockValues[cellValues.id]?.status ?? cellValues.status)}
          value={props.editBlockValues[cellValues.id]?.status ?? cellValues.status}
          mainValue={STATUS_OPEN}
          handleClick={(v) => props.handleChangeEditValue(v, "status", { ...cellValues, ...(props.editBlockValues[cellValues.id] ?? {}) })}
          style={{ width: "65px" }}
        />
      </div>
      <div className='item'>
        <Select
          values={filterAllowed({ [CALL_TYPE_IN]: t("Incoming"), [CALL_TYPE_OUT]: t("Outgoing"), [CALL_TYPE_ALL]: t("Both") }, allowedBlockCallTypes(), props.editBlockValues[cellValues.id]?.call_type ?? cellValues.call_type)}
          value={props.editBlockValues[cellValues.id]?.call_type ?? cellValues.call_type}
          handleClick={(v) => props.handleChangeEditValue(v, "call_type", { ...cellValues, ...(props.editBlockValues[cellValues.id] ?? {}) })}
        />
      </div>
      <div className='item note'>
        <input type="text" className='underline' name="note"
          value={props.editBlockValues[cellValues.id]?.note ?? cellValues.note}
          onChange={(e) => props.handleChangeEditValue(e.target.value, "note", { ...cellValues, ...(props.editBlockValues[cellValues.id] ?? {}) })}
        />
      </div>
      <div className='item save'>
        <img src='/design-images/save-black.svg' onClick={() => props.handleEditLineSubmit(cellValues.id)} alt="save" />
      </div>
    </>
  );

  // The community list endpoint returns a bogus sentinel total (e.g. 10000000);
  // fall back to the category's real number count (active_setting) in that case.
  const numbersCount = (props.rowCount != null && props.rowCount < 1000000)
    ? props.rowCount
    : (props.selectedCategory.active_setting != null ? props.selectedCategory.active_setting : props.rows.length);

  return (
    <div className="cat-detail-wrap" dir="rtl">

      {/* ── Back button ── */}
      <button className="cat-back-btn" onClick={props.viewCategoriesList}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
        חזרה לניהול חסימות
      </button>

      {/* ── Category header card ── */}
      <div className="cat-detail-header">
        <div className="cat-detail-header-main">
          <div className="cat-detail-icon-wrap">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
          <div className="cat-detail-title-block">
            <h2 className="cat-detail-title">{props.selectedCategory.name}</h2>
            {props.selectedCategory.note
              ? <p className="cat-detail-note">{props.selectedCategory.note}</p>
              : null}
          </div>
          <div className="cat-detail-badges">
            {props.selectedCategory.valid === 1 && (
              <span className="cat-badge cat-badge--green">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                מאומת
              </span>
            )}
            <span className="cat-badge">
              {props.selectedCategory.public === 1 ? 'ציבורי' : 'פרטי'}
            </span>
          </div>
        </div>
      </div>

      {/* ── Numbers card ── */}
      <div className="cat-detail-card">
        <div className="cat-detail-card-head">
          <div>
            <span className="cat-detail-count">{Number(numbersCount).toLocaleString('he-IL')}</span>
            <span className="cat-detail-count-label"> מספרים ברשימה</span>
          </div>
          <div className="cat-detail-actions">
            <div className="cat-head-search">
              <input
                type="text"
                placeholder={t("Search number in category")}
                value={props.searchInput.number}
                onChange={(e) => props.changeSearchFilters(e.target.value, "number")}
                onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
              />
              {props.searchInput.number ? (
                <button className="cat-head-search-clear" onClick={clearSearch} title={t('Cancel')}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              ) : null}
              <button className="cat-head-search-go" onClick={runSearch} title={t('Search')}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </button>
            </div>
            {props.selectionIds.length === 0 ? (
              <button className="cat-action-btn cat-action-btn--add" onClick={props.handleAddClick}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                {t("Add a row")}
              </button>
            ) : (
              <button className="cat-action-btn cat-action-btn--del" onClick={() => props.setDeleteDialogIsOpen(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                {t("Delete selected rows")}
              </button>
            )}

            {/* Import — click to open the menu (was hover-only, which was hard to hit) */}
            <div className="cat-import-wrap">
              <button
                className={"cat-action-btn cat-action-btn--import" + (importHover !== null ? " is-open" : "")}
                onClick={() => setImportHover(importHover !== null ? null : IMPORT_HOVER_UPLOAD)}
              >
                <svg width="14" height="14" viewBox="0 0 15 17" fill="none"><path d="M7.33185 1.00373L11.5702 5.24206M7.33185 1.00373L7.33185 11.5M7.33185 1.00373L3.09574 5.24075M13.5004 12.1256L13.5003 15.3278L0.985662 15.3277L0.985682 12.1257" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                העלאת רשימת מספרים
              </button>
              {importHover !== null && (
                <>
                  <div className="cat-import-overlay" onClick={() => setImportHover(null)} />
                  <div className="cat-import-menu">
                    <button className="cat-import-item" onClick={() => { setImportHover(null); props.onImportRows(); }}>
                      <svg width="13" height="13" viewBox="0 0 15 17" fill="none"><path d="M7.33185 1.00373L11.5702 5.24206M7.33185 1.00373L7.33185 11.5M7.33185 1.00373L3.09574 5.24075M13.5004 12.1256L13.5003 15.3278L0.985662 15.3277L0.985682 12.1257" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      {t("Import numbers file")}
                    </button>
                    <a className="cat-import-item" href="/מספרים לחסימה.xlsx" onClick={() => setImportHover(null)}>
                      <svg width="13" height="13" viewBox="0 0 15 17" fill="none"><path d="M7.33296 11.4933L3.09463 7.25497M7.33296 11.4933L7.33296 0.997025M7.33296 11.4933L11.5691 7.25627M13.4993 12.1189L13.4992 15.3211L0.984559 15.321L0.984579 12.119" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      {t("Download import file sample")}
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="grid-wrapper">
          {props.loading && (
            <div className="cat-grid-loading">
              <span className="cat-grid-spinner" />
              <span className="cat-grid-loading-text">טוען מספרים...</span>
            </div>
          )}
          <DesktopDataGrid
            rowCount={displayCount}
            rows={displayRows}
            loading={props.loading}
            columns={columns}
            page={props.page}
            pageSize={props.pageSize}
            onPageChange={(newPage) => props.setPage(newPage)}
            onPageSizeChange={(newPageSize) => props.setPageSize(newPageSize)}
            onDeleteRows={() => props.setDeleteDialogIsOpen(true)}
            setSelectionIds={props.setSelectionIds}
            selectionIds={props.selectionIds}
            renderRowExpansion={renderRowExpansion}
            expandedRowIds={props.expandedRowIds}
            setExpandedRowIds={props.setExpandedRowIds}
            isRowHighlighted={(row) => props.lineNumbersToHighlight.includes(row.number)}
          />
        </div>
      </div>

      {/* ── Add form ── */}
      <div className={'add-form-wrapper' + (props.isAddFormVisible ? "" : " hidden")}>
        <div className='add-form'>
          <div className='item number'>
            <input type="text" className='underline' name="number"
              value={props.addValue.number}
              onChange={(e) => { if (props.isValidValue(e.target.value, "number")) props.setAddValue({ ...props.addValue, "number": e.target.value }); }}
            />
          </div>
          <div className='item'>
            <Switch type="boolean" name="active"
              value={props.addValue.active} mainValue={1}
              handleClick={(v) => props.setAddValue({ ...props.addValue, "active": v })}
              style={{ width: "50px" }}
            />
          </div>
          <div className='item'>
            <Switch type="text" name="status"
              values={filterAllowed({ [STATUS_BLOCKED]: t("Blocked"), [STATUS_OPEN]: t("Open") }, allowedBlockStatuses())}
              value={props.addValue.status} mainValue={STATUS_OPEN}
              handleClick={(v) => props.setAddValue({ ...props.addValue, "status": v })}
              style={{ width: "65px" }}
            />
          </div>
          <div className='item'>
            <Select
              values={filterAllowed({ [CALL_TYPE_IN]: t("Incoming"), [CALL_TYPE_OUT]: t("Outgoing"), [CALL_TYPE_ALL]: t("Both") }, allowedBlockCallTypes())}
              value={props.addValue.call_type}
              handleClick={(v) => props.setAddValue({ ...props.addValue, "call_type": v })}
            />
          </div>
          <div className='item note'>
            <input type="text" className='underline' name="note"
              value={props.addValue.note}
              onChange={(e) => props.setAddValue({ ...props.addValue, "note": e.target.value })}
            />
          </div>
          <div className='item save'>
            <div className='button-wrapper' onClick={props.handleAddLineSubmit}>
              <svg width="19" height="19" viewBox="0 0 19 19" fill="none">
                <path d="M9.5 1.5V17.5M17.5 9.5H1.5" stroke="#FFC700" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
